import React, { FormEventHandler, useState } from 'react'
import styled from 'styled-components/macro'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useDeleteFrameworkRequirement } from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { RequirementInfo } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import { ModalForm, ModalFormId } from '../..'

const WarningList = styled.ul`
  margin: 12px 0 0;
  padding-left: 20px;
`

type Props = {
  selectedRequirement: RequirementInfo | null
  clearSelectedRequirement: () => void
}

export const DeleteFrameworkRequirementConfirmModal = ({
  selectedRequirement,
  clearSelectedRequirement,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM)
  const hideModal = useHideModal({
    modalId: ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM,
  })
  const deleteFrameworkRequirement = useDeleteFrameworkRequirement()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeAndReset = () => {
    hideModal()
    setIsSubmitting(false)
    clearSelectedRequirement()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    const modelId = selectedRequirement?.modelId
    if (!selectedRequirement || !modelId) return
    setIsSubmitting(true)
    try {
      await deleteFrameworkRequirement(modelId)
      toast.success('Requirement deleted.')
      closeAndReset()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('DeleteFrameworkRequirementConfirmModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM}
      size="lg"
      title="Delete requirement?"
      submitText="Delete requirement"
      disableSubmitButton={isSubmitting || !selectedRequirement}
    >
      <form
        id={ModalFormId.DELETE_FRAMEWORK_REQUIREMENT_CONFIRM}
        onSubmit={onSubmit}
      >
        {selectedRequirement?.name
          ? `"${selectedRequirement.name}"`
          : 'This requirement'}{' '}
        will be permanently deleted and unlinked from any linked controls.
        <WarningList>
          <li>This action cannot be undone.</li>
          <li>
            The requirement will be removed from any controls it&apos;s
            currently linked to.
          </li>
        </WarningList>
      </form>
    </ModalForm>
  )
}
